import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { accountingApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

type AcctType = 'asset'|'liability'|'equity'|'revenue'|'expense'|'cogs';
const TYPES: Array<{ key: AcctType; label: string; accent: string }> = [
  { key: 'asset',     label: 'Assets',    accent: 'bg-finance-500' },
  { key: 'liability', label: 'Liabilities',accent: 'bg-amber-500' },
  { key: 'equity',    label: 'Equity',    accent: 'bg-purple-500' },
  { key: 'revenue',   label: 'Revenue',   accent: 'bg-sage-500' },
  { key: 'expense',   label: 'Expenses',  accent: 'bg-danger' },
  { key: 'cogs',      label: 'COGS',      accent: 'bg-orange-500' },
];
const NORMAL: Record<AcctType, 'debit'|'credit'> = {
  asset: 'debit', expense: 'debit', cogs: 'debit',
  liability: 'credit', equity: 'credit', revenue: 'credit',
};

export function ChartOfAccountsPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<AcctType>('asset');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const { data, isLoading, error } = useQuery({ queryKey: ['accounts'], queryFn: () => accountingApi.listAccounts() });

  const createMut = useMutation({
    mutationFn: (p: any) => accountingApi.createAccount(p),
    onSuccess: () => { toast.success('Account created'); qc.invalidateQueries({ queryKey: ['accounts'] }); setShowCreate(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: any) => accountingApi.updateAccount(id, payload),
    onSuccess: () => { toast.success('Account updated'); qc.invalidateQueries({ queryKey: ['accounts'] }); setEditing(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, status }: any) => accountingApi.updateAccount(id, { status }),
    onSuccess: (_d, v) => { toast.success(v.status === 'active' ? 'Account activated' : 'Account deactivated'); qc.invalidateQueries({ queryKey: ['accounts'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { byType, counts, totalActive } = useMemo(() => {
    const rows = data ?? [];
    const grouped: Record<string, any[]> = {};
    const c: Record<string, number> = {};
    let active = 0;
    for (const a of rows) {
      (grouped[a.account_type] ??= []).push(a);
      c[a.account_type] = (c[a.account_type] ?? 0) + 1;
      if (a.status === 'active') active++;
    }
    return { byType: grouped, counts: c, totalActive: active };
  }, [data]);

  const filteredRows = useMemo(() => {
    const rows = byType[activeTab] ?? [];
    if (!search) return rows;
    const lower = search.toLowerCase();
    return rows.filter((a: any) => a.code.toLowerCase().includes(lower) || a.name.toLowerCase().includes(lower));
  }, [byType, activeTab, search]);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader eyebrow="Finance" title="Chart of accounts"
        subtitle="General ledger structure — assets, liabilities, equity, revenue, and expenses."
        actions={<button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New account</button>}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {TYPES.map((t) => (
          <div key={t.key} className="card p-4 relative overflow-hidden border-slate-200">
            <div className={`absolute top-0 left-0 right-0 h-0.5 ${t.accent}`} />
            <div className="eyebrow text-slate-500 text-2xs">{t.label}</div>
            <div className="mt-2 text-2xl font-bold font-display text-slate-900 tabular-nums">{counts[t.key] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Tab + search bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="card p-1 inline-flex gap-0.5 flex-shrink-0">
          {TYPES.map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={clsx('px-3 py-1.5 text-xs font-semibold rounded-md transition-all',
                activeTab === t.key ? 'bg-canvas-subtle text-slate-900' : 'text-slate-600 hover:text-slate-900')}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="card flex-1 p-1.5 flex items-center gap-2 px-3 min-w-48">
          <SearchIcon />
          <input type="text" placeholder="Search code or name…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-slate-400 py-1 min-w-0" />
        </div>
      </div>

      {isLoading ? <div className="card"><LoadingState rows={6} /></div>
        : error ? <ErrorState error={error} />
        : (
          <>
            <div className="flex items-center gap-3 mb-3 text-xs text-slate-500">
              <span className="font-semibold text-slate-900">{filteredRows.length}</span> accounts
              {search && <span>· filtered</span>}
              <span className="ml-auto">{totalActive} active total</span>
            </div>
            {!filteredRows.length ? (
              <div className="card border-slate-200">
                <EmptyState message={search ? 'No accounts match' : `No ${activeTab} accounts`}
                  hint={search ? 'Try a different search term.' : 'Add accounts of this type to build out the chart.'}
                  action={!search && <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New account</button>}
                />
              </div>
            ) : (
              <div className="card overflow-hidden border-slate-200">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Code</th>
                        <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Account name</th>
                        <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Currency</th>
                        <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Normal balance</th>
                        <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                        <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600 w-36">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((a: any, i: number) => (
                        <tr key={a.id} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
                          <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">{a.code}</td>
                          <td className="px-4 py-2 text-slate-900 font-medium">{a.name}</td>
                          <td className="px-4 py-2 font-mono text-xs text-slate-600">{a.currency}</td>
                          <td className="px-4 py-2"><BalanceChip balance={a.normal_balance} /></td>
                          <td className="px-4 py-2"><AccountStatusBadge status={a.status} /></td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button className="text-xs font-medium px-2 py-1 rounded hover:bg-slate-100 transition-colors text-slate-600"
                                onClick={() => setEditing(a)}>Edit</button>
                              <button
                                className={`text-xs font-medium px-2 py-1 rounded transition-colors ${a.status === 'active' ? 'text-slate-500 hover:bg-slate-100' : 'text-sage-700 hover:bg-sage-50'}`}
                                disabled={toggleMut.isPending}
                                onClick={() => toggleMut.mutate({ id: a.id, status: a.status === 'active' ? 'inactive' : 'active' })}>
                                {a.status === 'active' ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

      {showCreate && <AccountModal activeType={activeTab} onSubmit={(p) => createMut.mutate(p)} onClose={() => setShowCreate(false)} loading={createMut.isPending} accounts={data ?? []} />}
      {editing && <AccountModal initial={editing} activeType={editing.account_type} onSubmit={(p) => updateMut.mutate({ id: editing.id, payload: p })} onClose={() => setEditing(null)} loading={updateMut.isPending} accounts={data ?? []} />}
    </div>
  );
}

function AccountModal({ initial, activeType, onSubmit, onClose, loading, accounts }: {
  initial?: any; activeType: AcctType; onSubmit: (p: any) => void; onClose: () => void; loading: boolean; accounts: any[];
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    account_type: initial?.account_type ?? activeType,
    normal_balance: initial?.normal_balance ?? NORMAL[activeType],
    currency: initial?.currency ?? 'USD',
    is_bank_account: initial?.is_bank_account ?? false,
    parent_id: initial?.parent_id ?? '',
  });
  const sameTypeAccounts = accounts.filter((a: any) => a.account_type === form.account_type && a.id !== initial?.id);
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Finance · Chart of accounts</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{isEdit ? 'Edit account' : 'New account'}</h2>
      </div>
      <div className="px-6 py-5 grid grid-cols-2 gap-4">
        <div>
          <label className="label">Account code <span className="text-danger">*</span></label>
          <input className="input font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="1100" disabled={isEdit} />
        </div>
        <div>
          <label className="label">Account type</label>
          <select className="input" value={form.account_type}
            onChange={(e) => { const t = e.target.value as AcctType; setForm({ ...form, account_type: t, normal_balance: NORMAL[t] }); }}
            disabled={isEdit}>
            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Account name <span className="text-danger">*</span></label>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cash and cash equivalents" />
        </div>
        <div>
          <label className="label">Normal balance</label>
          <select className="input" value={form.normal_balance} onChange={(e) => setForm({ ...form, normal_balance: e.target.value as any })} disabled={isEdit}>
            <option value="debit">Debit</option>
            <option value="credit">Credit</option>
          </select>
        </div>
        <div>
          <label className="label">Currency</label>
          <select className="input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            {['USD','EUR','GBP','JPY','CAD','AUD','AED','SAR'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Parent account <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <select className="input" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
            <option value="">— No parent —</option>
            {sameTypeAccounts.map((a: any) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2.5 text-sm text-ink-700 cursor-pointer">
            <input type="checkbox" checked={form.is_bank_account} onChange={(e) => setForm({ ...form, is_bank_account: e.target.checked })} disabled={isEdit} />
            <div>
              <div className="font-medium">Bank account</div>
              <div className="text-2xs text-ink-500 mt-0.5">Links to bank reconciliation module.</div>
            </div>
          </label>
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading || !form.code || !form.name}
          onClick={() => onSubmit(isEdit ? { name: form.name } : {
            code: form.code, name: form.name, account_type: form.account_type,
            normal_balance: form.normal_balance, currency: form.currency,
            is_bank_account: form.is_bank_account, parent_id: form.parent_id || undefined,
          })}>
          {loading ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create account')}
        </button>
      </div>
    </Modal>
  );
}

function BalanceChip({ balance }: { balance: string }) {
  const isDebit = balance === 'debit';
  return (
    <span className={clsx('inline-flex items-center gap-1.5 font-mono text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded',
      isDebit ? 'bg-finance-50 text-finance-700 ring-1 ring-inset ring-finance-200' : 'bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200')}>
      <span className={clsx('w-1 h-1 rounded-full', isDebit ? 'bg-finance-500' : 'bg-sage-500')} />
      {balance}
    </span>
  );
}
function AccountStatusBadge({ status }: { status: string }) {
  const cls = status === 'active' ? 'bg-sage-50 text-sage-700 ring-sage-200' : 'bg-canvas-subtle text-slate-500 ring-slate-200';
  return <span className={clsx('inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset', cls)}>{status}</span>;
}

const ip = { width:16,height:16,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:2,strokeLinecap:'round' as const,strokeLinejoin:'round' as const };
function PlusIcon() { return <svg {...ip}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon() { return <svg {...ip} className="text-slate-400 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
